export const SAVE_DELAY = 2000;

export function debounce(func, delay) {
  let timeoutId;

  // eslint-disable-next-line func-names
  return function(...args) {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
}

export const getKey = (id, attr) => `${id}-${attr}`;

const getOptionBackgroundColor = (state) => {
  if (state.isSelected) return 'var(--sh-selected-item-bg)';
  if (state.isFocused) return 'inherit';

  return 'inherit';
};

const getOptionOutline = (state) => {
  if (state.isSelected) return 'none';

  return '1px solid var(--sh-outline)';
};

const getControlBorder = (state) => {
  if (state.isFocused || state.isSelected) return '1px solid var(--sh-outline)';

  return '1px solid var(--sh-border-color)';
};

export const canStyleFont = (element) => (
  ['p', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(element.tag)
);

export const canStyleDisplay = (element) => (
  ['div'].includes(element.tag)
);

export const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'var(--sh-background-base)',
    border: getControlBorder(state),
    borderRadius: 2,
    boxShadow: 'none',
    color: 'var(--sh-ui-font-color)',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: 'var(--sh-icon-color)',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    backgroundColor: 'var(--sh-icon-color)',
  }),
  menu: (provided) => ({
    ...provided,
    position: 'relative',
  }),
  menuList: (provided) => ({
    ...provided,
    backgroundColor: 'var(--sh-background-base)',
    borderRadius: 2,
    maxHeight: 200,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: getOptionBackgroundColor(state),
    borderRadius: 2,
    padding: 'var(--spacing)',
    cursor: 'pointer',
    '&:hover': {
      outline: getOptionOutline(state),
      outlineOffset: -1,
    },
  }),
  input: (provided) => ({
    ...provided,
    color: 'var(--sh-ui-font-color)',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'var(--sh-ui-font-color)',
    padding: '.25rem 0',
  }),
};

export const customSidebarSelectStyles = {
  container: (provided) => ({
    ...provided,
    border: 'none',
    fontSize: '.8rem',
  }),
  control: (provided) => ({
    ...provided,
    backgroundColor: 'var(--sh-background-base)',
    border: 'none',
    boxShadow: 'none',
    color: 'var(--sh-sidebar-font-color)',
    cursor: 'pointer',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: 'var(--sh-icon-color)',
    svg: {
      height: 15,
      width: 15,
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  menu: (provided) => ({
    ...provided,
    marginTop: 0,
    top: '-50%',
  }),
  menuList: (provided) => ({
    ...provided,
    backgroundColor: 'var(--sh-dropdown-background-base)',
    borderRadius: 2,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: getOptionBackgroundColor(state),
    borderRadius: 2,
    padding: 'calc(var(--spacing) * .5)',
    cursor: 'pointer',
    '&:hover': {
      outline: getOptionOutline(state),
      outlineOffset: -1,
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'var(--sh-icon-color)',
  }),
  input: (provided) => ({
    ...provided,
    color: 'var(--sh-sidebar-font-color)',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: 'var(--sh-sidebar-font-color)',
    padding: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    paddingLeft: 0,
  }),
};

export function kebabToCamel(obj = {}) {
  const res = {};

  // eslint-disable-next-line guard-for-in
  for (const key in obj) {
    const newKey = key.split('-').map((substr, i) => {
      if (i === 0) return substr;

      return `${substr.charAt(0).toUpperCase()}${substr.slice(1)}`;
    }).join('');

    res[newKey] = obj[key];
  }

  return res;
}

export function valueToLabelObj(options) {
  return options.reduce((acc, cur) => {
    acc[cur.value] = cur.label;

    return acc;
  }, {});
}

export function isGridDisplay(styles) {
  return styles.display === 'grid';
}

export function isFlexDisplay(styles) {
  return styles.display === 'flex';
}

export function isFlexOrGridDisplay(styles) {
  return isGridDisplay(styles) || isFlexDisplay(styles);
}

export function convertKeysToUnderscore(obj) {
  const convertedObj = {};
  for (let key in obj) {
    if (obj.hasOwnProperty(key) && obj[key] !== undefined) {
      const underscoreKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();
      convertedObj[underscoreKey] = obj[key];
    }
  }
  return convertedObj;
}
